import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import PMStatus from './PMStatus';
import PMProgress from './PMProgress';
import CodeBlock from '../CodeBlock/CodeBlock';
import FilePreview from './FilePreview';
import PMTable from './PMTable';
import './PMRenderer.css';

const PMList = ({ children, type = 'auto', className = '', ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const contentString = useMemo(() => {
    if (typeof children === 'string') {
      return children;
    }
    if (Array.isArray(children)) {
      return children
        .filter(child => typeof child === 'string' || typeof child === 'number')
        .join('');
    }
    if (children != null) {
      return String(children);
    }
    return '';
  }, [children]);

  const { truncatedContent, totalItems, shouldTruncate } = useMemo(() => {
    if (!contentString.trim()) {
      return {
        truncatedContent: '',
        totalItems: 0,
        shouldTruncate: false,
      };
    }

    const lines = contentString.split('\n').filter(line => line.trim());
    const listItemRegex = /^[\s]*(?:[•\-\*]|\d+\.)\s+/;
    const listItems = lines.filter(line => listItemRegex.test(line.trim()));
    
    const totalCount = listItems.length;
    const shouldLimit = totalCount > 10 && !isExpanded;
    
    if (shouldLimit) {
      const limitedItems = listItems.slice(0, 10);
      const truncated = limitedItems.join('\n');
      return {
        truncatedContent: truncated,
        totalItems: totalCount,
        shouldTruncate: true
      };
    }
    
    return {
      truncatedContent: contentString,
      totalItems: totalCount,
      shouldTruncate: false
    };
  }, [contentString, isExpanded]);
  
  const CustomListItem = React.useCallback(({ children, ...props }) => (
    <li className="mb-2 text-[0.875rem] text-[#0e0f11] dark:text-[#e5e7eb] leading-relaxed" {...props}>
      {children}
    </li>
  ), []);
  
  const CustomOrderedList = React.useCallback(({ children, ...props }) => (
    <ol className="list-decimal list-outside space-y-2 ml-6 pl-2" {...props}>
      {children}
    </ol>
  ), []);
  
  const CustomUnorderedList = React.useCallback(({ children, ...props }) => (
    <ul className="list-disc list-outside space-y-2 ml-6 pl-2" {...props}>
      {children}
    </ul>
  ), []);

  const CustomStrong = React.useCallback(({ children, ...props }) => (
    <strong className="font-black text-[#111827] dark:text-white" {...props}>
      {children}
    </strong>
  ), []);

  const CustomParagraph = React.useCallback(({ children, ...props }) => (
    <p className="mb-3 text-[0.875rem] text-[#111827] dark:text-[#e5e7eb] leading-relaxed" {...props}>
      {children}
    </p>
  ), []);

  const CustomCode = React.useCallback(({ children, ...props }) => (
    <code className="bg-gray-100 dark:bg-gray-800 text-[0.8rem] text-[#111827] dark:text-[#e5e7eb] px-1.5 py-0.5 rounded font-mono" {...props}>
      {children}
    </code>
  ), []);

  const CustomEM = React.useCallback(({ children, ...props }) => (
    <em className="italic text-[#4b5563] dark:text-[#d1d5db]" {...props}>
      {children}
    </em>
  ), []);

  const CustomMark = React.useCallback(({ children, ...props }) => (
    <mark className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded text-gray-900 dark:text-gray-100" {...props}>
      {children}
    </mark>
  ), []);

  const CustomLink = React.useCallback(({ children, href, ...props }) => {
    if (!href) {
      return <a {...props}>{children}</a>;
    }


    const textFromChildren = React.Children.toArray(children)
      .map(c => (typeof c === 'string' ? c : ''))
      .join('');
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textFromChildren.trim());
    const isMailto = href.startsWith('mailto:');
    if (looksLikeEmail || isMailto) {
      const { className: passedClassName, ...restProps } = props || {};
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-blue-600 dark:text-blue-400 hover:underline ${passedClassName || ''}`}
          {...restProps}
        >
          {children}
        </a>
      );
    }

    const fileExtension = href.split('.').pop()?.toLowerCase() || '';
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(fileExtension);
    const isPdf = fileExtension === 'pdf';

    if (isImage || isPdf) {
      const labelText = React.Children.toArray(children).map(c => (typeof c === 'string' ? c : '')).join('').trim();
      const altText = labelText || href.split('/').pop();
      const isCitationStylePDF = isPdf && /^\[\s*pdf\s*\]/i.test(labelText);
      if (isImage) {
        return <FilePreview src={href} alt={altText} type="image">{children}</FilePreview>;
      }
      if (isPdf && !isCitationStylePDF) {
        return <FilePreview src={href} alt={altText} type="pdf">{children}</FilePreview>;
      }
    }
 
    let displayHost = '';
    let isGithubCommitOrPR = false;
    try {
      const urlObj = new URL(href);
      displayHost = urlObj.hostname;
      const path = urlObj.pathname || '';
      const pathSegments = path.split('/').filter(Boolean);
      const isGithub = /(^|\.)github\.com$/i.test(displayHost);
      isGithubCommitOrPR = isGithub && (pathSegments.includes('commit') || pathSegments.includes('pull'));
    } catch {}

    if (isGithubCommitOrPR) {
      const { className: passedClassName, ...restProps } = props || {};
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-blue-600 dark:text-blue-400 hover:underline ${passedClassName || ''}`}
          {...restProps}
        >
          {children}
        </a>
      );
    }
    const shortHost = (displayHost || '').replace(/^www\./, '');
    const faviconSrc = displayHost ? `https://www.google.com/s2/favicons?domain=${displayHost}&sz=64` : '';
    const fullText = React.Children.toArray(children).map(c => (typeof c === 'string' ? c : '')).join('') || href;

    return (
      <span className="research-pill-wrap">
        <a href={href} target="_blank" rel="noopener noreferrer" className="research-pill" {...props}>
          {faviconSrc ? <img src={faviconSrc} alt="" className="w-3 h-3 rounded-full inline-block" /> : null}
          <span className="ml-1 opacity-70 truncate max-w-[100px]">{shortHost}</span>
        </a>
        <span className="pill-tooltip">
          <div className="pill-tooltip-card">
            <div className="pill-tooltip-header">
              {faviconSrc ? <img src={faviconSrc} alt="" className="pill-tooltip-favicon" /> : null}
              <span className="pill-tooltip-domain">{displayHost}</span>
            </div>
            <div className="pill-tooltip-title">{fullText}</div>
          </div>
        </span>
      </span>
    );
  }, []);

  const CustomImage = React.useCallback(({ src, alt }) => {
    return <FilePreview src={src} alt={alt} type="image" />;
  }, []);
  
  const markdownComponents = useMemo(() => ({
    li: CustomListItem,
    ol: CustomOrderedList,
    ul: CustomUnorderedList,
    strong: CustomStrong,
    b: CustomStrong,
    p: CustomParagraph,
    code: CustomCode,
    em: CustomEM,
    i: CustomEM,
    mark: CustomMark,
    a: CustomLink,
    img: CustomImage,
    pre: ({ children }) => {
        const codeElement = React.Children.only(children);
        const codeContent = codeElement.props.children || '';
        const langClassName = codeElement.props.className || '';
        return (
            <CodeBlock className={langClassName}>
                {codeContent}
            </CodeBlock>
        );
    },
  }), [CustomListItem, CustomOrderedList, CustomUnorderedList, CustomStrong, CustomParagraph, CustomCode, CustomEM, CustomMark, CustomLink, CustomImage]);

  const parsedContent = useMemo(() => {
    const contentToParse = shouldTruncate ? truncatedContent : contentString;
    if (!contentToParse.trim()) {
      return null;
    }

    const elements = [];
    let currentIndex = 0;

    const regex = /<pm:(status|progress|file|table)(?:\s+([^>]*?))?>(.*?)<\/pm:\1>/gs;

    let match;
    while ((match = regex.exec(contentToParse)) !== null) {
      const matchIndex = match.index;
      if (matchIndex > currentIndex) {
        const textBefore = contentToParse.substring(currentIndex, matchIndex);
        elements.push(
          <ReactMarkdown
            key={`text-${currentIndex}`}
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {textBefore}
          </ReactMarkdown>
        );
      }
      
      const key = `pm-tag-${matchIndex}`;
      const tagName = match[1];
      const attributes = match[2] || '';
      let innerContent = match[3] || '';

      const attrs = {};
      if (attributes) {
        const attrRegex = /(\w+)="([^"]*?)"/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(attributes)) !== null) {
          attrs[attrMatch[1]] = attrMatch[2];
        }
      }

      // Clean up the content to remove excessive indentation
      innerContent = innerContent.replace(/^\n+/, '').replace(/\n+$/, '');
      const lines = innerContent.split('\n');
      if (lines.length > 1) {
        const nonEmptyLines = lines.filter(line => line.trim().length > 0);
        if (nonEmptyLines.length > 0) {
          const minIndent = Math.min(...nonEmptyLines.map(line => {
            const indentMatch = line.match(/^(\s*)/);
            return indentMatch ? indentMatch[1].length : 0;
          }));
          if (minIndent > 0) {
            innerContent = lines.map(line => 
              line.length >= minIndent ? line.substring(minIndent) : line
            ).join('\n');
          }
        }
      }

      switch (tagName) {
        case 'status':
          elements.push(<PMStatus key={key} type={attrs.type || 'info'}>{innerContent}</PMStatus>);
          break;
        case 'progress':
          elements.push(<PMProgress key={key} value={attrs.value || 0} />);
          break;
        case 'file':
          elements.push(<FilePreview key={key} src={attrs.src} alt={attrs.alt} type={attrs.type}>{innerContent}</FilePreview>);
          break;
        case 'table':
          elements.push(<PMTable key={key} {...attrs}>{innerContent}</PMTable>);
          break;
        default:
          break;
      }

      currentIndex = match.index + match[0].length;
    }

    if (currentIndex < contentToParse.length) {
      const remainingText = contentToParse.substring(currentIndex);
      elements.push(
        <ReactMarkdown
          key={`text-${currentIndex}`}
          components={markdownComponents}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
        >
          {remainingText}
        </ReactMarkdown>
      );
    }
    
    return elements;

  }, [contentString, shouldTruncate, truncatedContent, markdownComponents]);
  
  if (!contentString.trim()) {
    return null;
  }
  return (
    <div className={`pm-list pl-1 py-2 my-3 ${className}`} {...props}>
      {parsedContent}
      
      {totalItems > 10 && (
        <div className="mt-2 text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-300 ease-out transform hover:scale-105"
          >
            {isExpanded ? (
              <>
                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">Show Less</span>
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid ">
                  View more items
                </span>
                <div className="relative w-4 h-4 flex flex-col items-center justify-center overflow-hidden">
                  <div className="arrow-sliding">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-2">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-3">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default PMList;